import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';
import type { Socket } from 'socket.io-client';
import { isFlowHttpMutationsEnabled } from '@/config/flow-mutations';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';
import {
  buildApplySyncBodyFromFlowDocument,
  buildMinimalFlowSyncWirePayload,
  postApplyFlowMutationWithConflictRetry,
} from '@/state/mutations/flows/apply-flow-mutation.http';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { normalizeFlowForPlanView } from '@/components/flows/detail/normalize-flow-plan-view';
import { mergeFlowPreservingLiveContext } from '@/helpers/merge-flow-preserving-live-context.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';

let warnedLegacySocketPath = false;

function warnLegacySocketPathOnce(): void {
  if (!import.meta.env.DEV || warnedLegacySocketPath) {
    return;
  }
  warnedLegacySocketPath = true;
  console.warn(
    '[emitFlowSyncUnified] WebSocket flowSync path is deprecated. Set VITE_FLOW_HTTP_MUTATIONS=true (see .env.example).',
  );
}

export type FlowSyncEmitResult = { ok: boolean; flow?: FlowDto };

export type FlowSyncClientPayload = {
  flow: Record<string, unknown>;
};

function invalidateFlowRelatedQueries(queryClient: QueryClient, flowQueryKey: QueryKey) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: flowQueryKey }),
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
    invalidateSimulationCueSheet(queryClient),
    invalidatePublicationStateQueries(queryClient),
  ]);
}

/**
 * Replaces `socket.emit('flowSync', { flow }, cb)` when {@link isFlowHttpMutationsEnabled} is true.
 * On success, invalidates the same React Query keys as useUpdateFlowMutation.
 */
export async function emitFlowSyncUnified(options: {
  socket: Socket | null | undefined;
  isConnected: boolean;
  payload: FlowSyncClientPayload;
  flowId: string;
  token: string;
  queryClient: QueryClient;
  queryKey: QueryKey;
  /** Use for save / double-submit risky actions (e.g. form submit). */
  idempotencyKey?: string;
}): Promise<FlowSyncEmitResult> {
  const { socket, isConnected, payload, flowId, token, queryClient, queryKey, idempotencyKey } = options;

  if (!isFlowHttpMutationsEnabled()) {
    warnLegacySocketPathOnce();
    if (!socket || !isConnected) {
      return { ok: false };
    }
    let wirePayload: FlowSyncClientPayload;
    try {
      wirePayload = {
        flow: buildMinimalFlowSyncWirePayload({ ...payload.flow } as Record<string, unknown>),
      };
    } catch (e) {
      console.error('[emitFlowSyncUnified] minimal flowSync payload failed', e);
      return { ok: false };
    }
    return new Promise((resolve) => {
      socket.emit('flowSync', wirePayload, (response: { ok: boolean } | undefined) => {
        const ok = !!response?.ok;
        if (ok) {
          void invalidateFlowRelatedQueries(queryClient, queryKey);
        }
        resolve({ ok });
      });
    });
  }

  try {
    const flowDoc = { ...payload.flow } as Record<string, unknown>;
    const serverFlow = await postApplyFlowMutationWithConflictRetry({
      flowId,
      token,
      queryClient,
      flowQueryKey: queryKey,
      idempotencyKey,
      buildBody: (serverFlowFromRefetch) => {
        if (serverFlowFromRefetch) {
          return buildApplySyncBodyFromFlowDocument({
            ...flowDoc,
            version: serverFlowFromRefetch.version,
            id: serverFlowFromRefetch.id ?? flowDoc.id,
          });
        }
        return buildApplySyncBodyFromFlowDocument(flowDoc);
      },
    });
    const normalizedFlow = mergeFlowPreservingLiveContext(
      normalizeFlowForPlanView(serverFlow, flowId),
      (queryClient.getQueryData(queryKey) as FlowDto | undefined) ??
        useFlowStore.getState().flowPlan ??
        useFlowStore.getState().flow,
    );
    queryClient.setQueryData(queryKey, normalizedFlow);
    await invalidateFlowRelatedQueries(queryClient, queryKey);
    return { ok: true, flow: normalizedFlow };
  } catch {
    return { ok: false };
  }
}
